import { resolveApiPath } from "@/lib/utils.ts";
import { resolveCommunicationFileSources } from "@/shared/communicationFilePaths.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import * as Sentry from "@sentry/react";
import DOMPurify from "dompurify";
import { useEffect, useRef } from "react";

type SandboxedHtmlComponentData = Extract<ArtifactComponent, { type: "sandboxedHtml" }>;

const PURIFY_CONFIG = {
  ADD_TAGS: ["style"],
  ADD_ATTR: ["target", "style", "href", "rel"],
};

// Force every link to open in a new top-level tab instead of navigating the
// sandboxed iframe in place. A DOMPurify hook runs at sanitize time and
// overrides any author-supplied target (e.g. `target="_self"`), which a
// `<base target="_blank">` default cannot. rel="noopener noreferrer" drops the
// opener handle and the Referer header.
const forceLinksToNewTab = (node: Element) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
};

const IFRAME_DOC_TEMPLATE = (sanitizedContent: string) => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        html, body {
          margin: 0;
          padding: 0;
          overflow-y: hidden;
        }
        body {
          font-family: system-ui, -apple-system, sans-serif;
          overflow-wrap: break-word;
          word-wrap: break-word;
          box-sizing: border-box;
          width: 100%;
        }
        * {
          max-width: 100%;
          box-sizing: border-box;
        }
        pre, code {
          white-space: pre-wrap;
          overflow-x: auto;
        }
        img {
          max-width: 100%;
          height: auto;
        }
        table {
          max-width: 100%;
          table-layout: fixed;
          word-break: break-word;
        }
        a {
          cursor: pointer;
          color: #0066cc;
          text-decoration: underline;
        }
        .email-quote {
          color: #666;
          border-left: 3px solid #ccc;
          padding-left: 10px;
          margin: 4px 0;
        }
        @media screen and (max-width: 600px) {
          table, td, tr {
            width: 100% !important;
            height: auto !important;
            display: block !important;
          }
          img {
            max-width: 100% !important;
            height: auto !important;
          }
        }
      </style>
    </head>
    <body>${sanitizedContent}</body>
  </html>
`;

// The regex requires a tag-name char to be followed by whitespace, `>`, or `/`
// so email-address fragments like `<user@example.com>` don't get mis-detected
// as HTML.
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(?:[\s>/])/i;

const groupQuotedLines = (text: string): string => {
  const lines = text.split(/\r\n|\r|\n/);
  const result: string[] = [];
  let quoteBuffer: string[] = [];

  const flush = () => {
    if (quoteBuffer.length > 0) {
      result.push(`<div class="email-quote">${quoteBuffer.join("<br>")}</div>`);
      quoteBuffer = [];
    }
  };

  for (const line of lines) {
    if (/^>+/.test(line)) {
      quoteBuffer.push(line.replace(/^>+\s?/, ""));
    } else {
      flush();
      result.push(line);
    }
  }
  flush();
  return result.join("<br>");
};

export const SandboxedHtmlBlock = ({ component }: { component: SandboxedHtmlComponentData }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { body, minHeightPx } = component;
  const isEmpty = body.trim().length === 0;

  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    const iframeDocument = iframeRef.current.contentWindow.document;

    // The server points inline images at the API by an origin-relative path,
    // which this iframe would otherwise load from the web app's own origin.
    const contentToSanitize = HTML_TAG_REGEX.test(body)
      ? resolveCommunicationFileSources(body, resolveApiPath)
      : groupQuotedLines(body);
    DOMPurify.addHook("afterSanitizeAttributes", forceLinksToNewTab);
    const sanitizedContent = DOMPurify.sanitize(contentToSanitize, PURIFY_CONFIG);
    DOMPurify.removeHook("afterSanitizeAttributes");

    // Mirrors the BIG-4199 instrumentation from CommunicationBody: when the
    // pre-sanitize body had content but DOMPurify stripped everything, the
    // user sees a blank iframe — surface it.
    if (body.trim().length > 0 && sanitizedContent.trim().length === 0) {
      console.error("SandboxedHtmlBlock: sanitization produced empty output", {
        inputLength: body.length,
        contentToSanitizeLength: contentToSanitize.length,
      });
      Sentry.captureException(new Error("SandboxedHtmlBlock: sanitization produced empty output"), {
        tags: { component: "SandboxedHtmlBlock" },
        extra: {
          inputLength: body.length,
          contentToSanitizeLength: contentToSanitize.length,
        },
      });
    }

    iframeDocument.open();
    iframeDocument.write(IFRAME_DOC_TEMPLATE(sanitizedContent));
    iframeDocument.close();

    const resizeObserver = new ResizeObserver(() => {
      if (iframeRef.current?.contentWindow) {
        const docBody = iframeRef.current.contentWindow.document.body;
        if (docBody) {
          iframeRef.current.style.height = `${docBody.scrollHeight}px`;
        }
      }
    });
    resizeObserver.observe(iframeDocument.body);
    return () => resizeObserver.disconnect();
  }, [body]);

  if (isEmpty) {
    return <div className="text-sm text-muted-foreground italic">(No content available)</div>;
  }

  return (
    <iframe
      ref={iframeRef}
      title="Sandboxed content"
      className="w-full border-0 overflow-hidden"
      style={{ minHeight: `${minHeightPx ?? 200}px`, width: "100%" }}
      // No `allow-scripts`: the frame shares the app's origin, so a script in it could lift its own sandbox.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
};
